import React from "react";
import { Link } from "react-router-dom";
import { Search, ShieldCheck, Wrench, Activity, ArrowRight, CheckCircle2 } from "lucide-react";
import MarketingHeader from "../components/argus/MarketingHeader";
import Footer from "../components/argus/Footer";
import CookieBanner from "../components/argus/CookieBanner";
import DesignPartnerDialog from "../components/argus/DesignPartnerDialog";
import { Button } from "../components/ui/button";
import { STATS, INTEGRATIONS, SOLUTIONS, INDUSTRIES } from "../lib/marketingContent";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/a87c6c22-683b-42aa-a16c-e2dbf14af51c/images/b066346698653eb2111156db4f1caef6c47a61533fdd31bd4ee5b6b2ee854770.png";
const SHIELD_IMG = "https://static.prod-images.emergentagent.com/jobs/a87c6c22-683b-42aa-a16c-e2dbf14af51c/images/6862d21924348ba4c40880283a979b306c685af4bbc057f0b67479b844bef49e.png";
const DATA_IMG = "https://static.prod-images.emergentagent.com/jobs/a87c6c22-683b-42aa-a16c-e2dbf14af51c/images/a7fc75d6283874b1ce5d53b89057ac42f414f704df0488d5525e46703c4fd9d8.png";

const features = [
  { icon: Search, title: "Find.", body: "Continuous SAST, DAST, and SCA scanning — across every commit, every deploy, every dependency." },
  { icon: ShieldCheck, title: "Prove.", body: "Every finding ships with raw evidence. Request, response, code line, CVE record. No black box." },
  { icon: Wrench, title: "Fix.", body: "A plain-English fix sits beside every flaw. Before / after code where the rule has one." },
  { icon: Activity, title: "Watch.", body: "Risk trends, MTTR, top vulnerable assets — surfaced on one dashboard your security lead actually reads." },
];

const steps = [
  { n: "01", title: "Point ArgusSecure at your code, app, or manifest.", body: "GitHub repo, web URL, or package.json. Three clicks." },
  { n: "02", title: "We scan. You sip coffee.", body: "SAST, DAST, and SCA run in parallel. Results stream in as findings are confirmed." },
  { n: "03", title: "Triage and fix.", body: "One panel: severity, evidence, exact fix. Mark fixed, accept risk, or file a ticket." },
];

export default function Landing() {
  return (
    <div className="bg-paper text-ink min-h-screen">
      <MarketingHeader />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-30" style={{ backgroundImage: "linear-gradient(rgba(201,169,97,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,97,0.06) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-paper via-paper to-paper" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-in-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-line text-xs font-medium text-ink/70 mb-6" data-testid="hero-eyebrow">
              <span className="w-2 h-2 rounded-full bg-safe animate-pulse" /> Built by GSGD FIELD SOLUTIONS ENGINEERING
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight" data-testid="hero-title">
              Hackers Are <span className="italic">Already</span> Looking. <br />
              <span className="text-gold">Look First.</span>
            </h1>
            <p className="mt-6 text-lg text-ink/70 max-w-xl" data-testid="hero-sub">
              Continuous SAST, DAST, and SCA scanning. Evidence on every finding. A fix beside every flaw. Built for teams who ship fast.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup">
                <Button className="bg-gold text-paper hover:bg-gold2 h-12 px-6 text-base font-semibold" data-testid="hero-start-scan">
                  Start My Free Scan <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/demo">
                <Button variant="outline" className="h-12 px-6 text-base border-line hover:bg-raised/50" data-testid="hero-live-demo">
                  See a Live Demo
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-ink/50">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-safe" /> No credit card</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-safe" /> Findings in 90s</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-safe" /> Cancel anytime</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-lg ring-1 ring-line aspect-[4/5]">
              <img src={HERO_BG} alt="ArgusSecure live scanning dashboard" className="w-full h-full object-cover" />
              <div className="scan-line" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-paper rounded-xl shadow-lg ring-1 ring-line p-4 w-56 hidden md:block" data-testid="hero-finding-card">
              <div className="flex items-center justify-between mb-2">
                <span className="sev-chip sev-critical">CRITICAL</span>
                <span className="text-[10px] text-ink/40 font-mono">+18s</span>
              </div>
              <div className="text-sm font-medium leading-tight">Log4Shell in <span className="font-mono text-xs">log4j-core@2.14.1</span></div>
              <div className="text-xs text-ink/50 mt-1">Fix: bump to ≥ 2.17.1</div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE GRID */}
      <section id="product" className="bg-paper border-t border-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl mb-12">
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight" data-testid="features-title">
              Your code. Your apps. Your dependencies. <span className="text-gold">One scanner.</span>
            </h2>
            <p className="mt-4 text-ink/70">Four things ArgusSecure does relentlessly, so you can ship without flinching.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="features-grid">
            {features.map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-paper border border-line rounded-xl p-6 hover:shadow-md transition-shadow" data-testid={`feature-${title.toLowerCase().replace(/\./g, "")}`}>
                <Icon size={28} className="text-gold mb-4" strokeWidth={2.25} />
                <h3 className="font-display text-2xl mb-2">{title}</h3>
                <p className="text-sm text-ink/70 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DESIGN-PARTNER ROSTER NOTICE (no fabricated logos) */}
      <section className="py-12 bg-paper border-t border-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p
            className="text-center text-ink/50"
            style={{
              fontVariant: "small-caps",
              letterSpacing: "0.22em",
              fontSize: "0.75rem",
            }}
            data-testid="design-partner-notice"
          >
            GSGD Field Solutions Engineering Services
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="solutions" className="bg-panel text-ink">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight" data-testid="how-title">How it works.</h2>
            <p className="mt-4 text-ink/60">Three steps. Five minutes. Real findings.</p>
            <div className="mt-8 rounded-2xl overflow-hidden ring-1 ring-paper/10 aspect-[4/3]">
              <img src={DATA_IMG} alt="ArgusSecure findings panel" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7 space-y-8">
            {steps.map(({ n, title, body }) => (
              <div key={n} className="grid grid-cols-[auto_1fr] gap-6 border-b border-line pb-8 last:border-0 last:pb-0">
                <span className="font-mono text-sm text-gold">{n}</span>
                <div>
                  <h3 className="font-display text-2xl mb-2">{title}</h3>
                  <p className="text-ink/60">{body}</p>
                </div>
              </div>
            ))}
            <Link to="/signup">
              <Button className="bg-gold hover:bg-gold2 text-paper h-12 px-6 text-base font-semibold" data-testid="how-cta">
                Show Me My Risks <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="bg-paper border-t border-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-6">
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight">Priced like a teammate. <br />Not a hostage situation.</h2>
              <p className="mt-4 text-ink/70">Free for one project. $49/mo for unlimited scans on 25 projects.</p>
              <Link to="/pricing" className="inline-block mt-6">
                <Button className="bg-gold text-paper hover:bg-gold2 h-12 px-6 font-semibold" data-testid="pricing-teaser-cta">
                  See Plans →
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-2xl overflow-hidden ring-1 ring-line shadow-md aspect-[5/4]">
                <img src={SHIELD_IMG} alt="ArgusSecure shield" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-panel border-t border-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="landing-stats">
          {STATS.map((s) => (
            <div key={s.label} className="text-center md:text-left" data-testid={`stat-${s.label.split(" ")[0].toLowerCase()}`}>
              <div className="font-display text-4xl md:text-5xl text-gold tracking-tight">{s.value}</div>
              <div className="text-xs uppercase tracking-wider text-ink/50 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SOLUTIONS GRID — only the two plays the scanner actually delivers today. */}
      <section className="bg-paper border-t border-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <p className="text-xs uppercase tracking-wider text-gold mb-2 font-semibold">Solutions</p>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight">Pick the play. Run it.</h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6" data-testid="solutions-grid">
            {[SOLUTIONS["application-security"], SOLUTIONS["devsecops"]].map((s) => (
              <Link
                key={s.slug}
                to={`/solutions/${s.slug}`}
                className="block rounded-xl border border-line bg-panel p-6 hover:bg-raised transition-colors"
                data-testid={`solution-card-${s.slug}`}
              >
                <h3 className="font-display text-2xl mb-2">{s.name}</h3>
                <p className="text-sm text-ink/60 leading-relaxed">{s.tagline}</p>
                <span className="inline-flex items-center gap-1 text-xs text-gold mt-4">Learn the play →</span>
              </Link>
            ))}
          </div>

          <div className="mt-14">
            <p className="text-[11px] uppercase tracking-wider text-ink/40 mb-4 font-semibold">On the roadmap</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="roadmap-grid">
              {[
                { name: "Compliance & Audit", note: "Roadmap. Joining the lineup once we ship audit-ready evidence exports." },
                { name: "Cloud & Container", note: "Roadmap. Container image SCA shipping next." },
                { name: "Exposure Management", note: "Roadmap." },
                { name: "AI / LLM Security", note: "Roadmap." },
              ].map((r) => (
                <div
                  key={r.name}
                  className="rounded-lg border border-line bg-paper p-4 cursor-not-allowed opacity-60"
                  data-testid={`roadmap-card-${r.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  aria-disabled="true"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h4 className="font-display text-lg text-ink">{r.name}</h4>
                    <span
                      className="text-[9px] uppercase tracking-wider text-gold border border-gold/40 bg-gold/10 rounded-full px-2 py-0.5 whitespace-nowrap"
                      data-testid={`roadmap-badge-${r.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      Coming soon
                    </span>
                  </div>
                  <p className="text-xs text-ink italic mt-1">{r.note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES STRIP */}
      <section className="bg-panel border-t border-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-xs uppercase tracking-wider text-gold mb-2 font-semibold">Industries</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight mb-10">Built for the regulated. Loved by the rest.</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="industries-grid">
            {Object.values(INDUSTRIES).map((i) => (
              <Link
                key={i.slug}
                to={`/industries/${i.slug}`}
                className="rounded-lg border border-line bg-paper p-4 text-sm hover:border-gold hover:text-gold transition-colors text-center"
                data-testid={`industry-chip-${i.slug}`}
              >
                {i.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOMER STORIES — coming soon (no fabricated testimonials per FTC 16 CFR §255). */}
      <section className="bg-panel border-t border-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20" data-testid="customer-stories-coming-soon">
          <p className="text-xs uppercase tracking-wider text-gold mb-2 font-semibold">Customer stories</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">Coming soon.</h2>
          <p className="text-ink/70 max-w-2xl text-lg">
            We're onboarding our first cohort of design partners now. Want to be quoted here?{" "}
            <DesignPartnerDialog
              source="landing-customer-stories"
              trigger={
                <button
                  type="button"
                  className="text-gold hover:text-gold2 underline underline-offset-2"
                  data-testid="customer-talk-to-security"
                >
                  Talk to Security →
                </button>
              }
            />
          </p>
        </div>
      </section>

      {/* INTEGRATIONS — built and shipping soon. No vapor logos. */}
      <section className="bg-paper border-t border-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider text-gold mb-2 font-semibold">Integrations</p>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Built and shipping soon.</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-8" data-testid="integrations-strip">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-ink/40 mb-3 font-semibold text-center">Live today</p>
              <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3" data-testid="integrations-live">
                {INTEGRATIONS.live.map((it) => (
                  <span
                    key={it.name}
                    className="font-display text-2xl text-ink"
                    title={it.note}
                    data-testid={`integration-live-${it.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {it.name}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wider text-ink/40 mb-3 font-semibold text-center">Shipping in 90 days</p>
              <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3" data-testid="integrations-coming">
                {INTEGRATIONS.coming.map((it) => (
                  <span
                    key={it.name}
                    className="font-display text-2xl text-ink"
                    style={{ opacity: 0.6 }}
                    title={it.note}
                    data-testid={`integration-coming-${it.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {it.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-panel border-t border-line">
        <div className="mx-auto max-w-4xl w-full px-4 sm:px-6 lg:px-8 py-20 text-center" data-testid="landing-final-cta">
          <h2 className="font-display text-5xl sm:text-6xl tracking-tight mb-4">Find it before they do.</h2>
          <p className="text-ink/60 mb-8 text-lg">Free for one project. No credit card. First finding in 90 seconds.</p>
          <Link to="/signup">
            <Button className="bg-gold text-paper hover:bg-gold2 h-14 px-8 font-semibold text-base">
              Start My Free Scan <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <CookieBanner />
    </div>
  );
}
